import React, { useContext, useState } from 'react';
import { Link, useStaticQuery, graphql } from 'gatsby';
import styled, { css } from 'styled-components';
import { SiteContext } from '../../context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMoon, faSun, faBars } from '@fortawesome/free-solid-svg-icons';
import { colors, primaryColors } from '../../theme';
import { device } from '../../theme';
import { ThemePicker } from '../ThemePicker';

const StyledNavigation = styled.nav`
  flex: 1 1;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  a + a {
    margin-left: 20px;
  }
`;

const StyledThemeSwitcher = styled.button`
  background-color: transparent;
  border: none;
  color: inherit;
  margin-left: 10px;
  height: 40px;
  font-size: 1.2rem;
`;

interface SLWProps {
  mobileMenuOpen: boolean;
}

const StyledLinkWrapper = styled.div<SLWProps>`
  display: flex;
  flex: 1 1 100%;
  justify-content: flex-end;
  a[aria-current='page'] {
    border-bottom: solid 3px ${({ theme }) => theme.primary};
  }
  @media ${device.tabletS} {
    position: absolute;
    width: calc(100% + 40px);
    top: 70px;
    background-color: ${({ theme }) => theme.background};
    border-bottom: solid 5px ${({ theme }) => theme.primary};
    flex-flow: column;
    padding: 30px;
    box-sizing: border-box;
    display: none;
    z-index: 9;
    left: -20px;
    overflow: hidden;
    a[aria-current='page'] {
      border-left: solid 3px ${({ theme }) => theme.primary};
      border-bottom: none;
      padding-left: 20px;
    }
    ${({ mobileMenuOpen }) =>
      mobileMenuOpen &&
      css`
        display: flex;
      `}
    a + a {
      margin-left: 0;
      margin-top: 20px;
    }
  }
`;

const StyledMobileMenuButton = styled.button`
  background-color: transparent;
  border: none;
  margin-left: 15px;
  height: 40px;
  display: none;
  font-size: 1.2rem;
  svg {
    color: ${({ theme }) => theme.text};
  }
  @media ${device.tabletS} {
    display: block;
  }
`;

const Navigation = () => {
  const { dispatch, globalState } = useContext(SiteContext);
  const query = useStaticQuery(graphql`
    query getStoriDataColorPicker {
      storiThemeData {
        showColorPicker
        basePath
      }
      allStoriPageMdx {
        edges {
          node {
            id
            slug
            title
            excerpt
            draft
            body
          }
        }
      }
    }
  `);
  const { showColorPicker, basePath } = query.storiThemeData;
  const { edges: pages } = query.allStoriPageMdx;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  return (
    <StyledNavigation>
      <StyledLinkWrapper mobileMenuOpen={mobileMenuOpen}>
        <Link to={basePath}>Home</Link>
        {pages.map((page: any) => {
          return (
            <Link to={`${basePath}/${page.node.slug}`} key={page.node.id}>
              {page.node.title}
            </Link>
          );
        })}
      </StyledLinkWrapper>
      <StyledMobileMenuButton
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
      >
        <FontAwesomeIcon icon={faBars} />
      </StyledMobileMenuButton>

      <StyledThemeSwitcher
        title={`Switch to ${
          globalState.themeMode === 'dark' ? 'light' : 'dark'
        } mode`}
        onClick={() => {
          dispatch({
            type: 'SET_THEME_MODE',
            payload: globalState.themeMode === 'dark' ? 'light' : 'dark',
          });
          window.localStorage.setItem(
            'stori_theme_mode',
            globalState.themeMode === 'dark' ? 'light' : 'dark'
          );
          const root = window.document.documentElement;
          root.style.setProperty(
            '--color-text',
            globalState.themeMode === 'dark'
              ? colors.light.text
              : colors.dark.text
          );
          root.style.setProperty(
            '--color-background',
            globalState.themeMode === 'dark'
              ? colors.light.background
              : colors.dark.background
          );
          root.style.setProperty(
            '--color-primary',
            globalState.themeMode === 'dark'
              ? primaryColors[globalState.theme].light.primary
              : primaryColors[globalState.theme].dark.primary
          );
          root.style.setProperty(
            '--color-button-text-primary',
            globalState.themeMode === 'dark'
              ? primaryColors[globalState.theme].light.primaryButtonText
              : primaryColors[globalState.theme].dark.primaryButtonText
          );
        }}
      >
        <FontAwesomeIcon
          icon={globalState.themeMode === 'dark' ? faSun : faMoon}
        />
      </StyledThemeSwitcher>
      {showColorPicker && <ThemePicker />}
    </StyledNavigation>
  );
};

export default Navigation;
